import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { useApp } from "@/context/AppContext";
import { fromNow } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ShieldAlert, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TONE = { CLEAR: "bg-success/10 text-success", REVIEW: "bg-warning/10 text-warning", BLOCKED: "bg-danger/10 text-danger" };

export function useConflictChecks() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/intake/conflict-checks`).then((r) => setData(r.data)).catch(() => setData({ checks: [], summary: {} })); }, []);
  return data;
}

export function ConflictDashboardCard() {
  const { can } = useApp();
  const navigate = useNavigate();
  const data = useConflictChecks();
  if (!can("team.manage") || !data) return null;
  const s = data.summary || {};
  return (
    <Panel data-testid="conflict-dashboard-card">
      <PanelHeader title="Intake Conflicts · last 7 days">
        <button onClick={() => navigate("/settings?section=Intake%20Conflicts")} className="flex items-center gap-1 text-xs text-copper hover:underline" data-testid="conflict-card-review-link">Review <ArrowRight className="h-3 w-3" /></button>
      </PanelHeader>
      <div className="grid grid-cols-3 divide-x divide-line p-2 text-center">
        {[["Blocked", s.blocked7d, "text-danger"], ["Overridden", s.overridden7d, "text-warning"], ["Awaiting partner", s.pending, "text-ink"]].map(([k, v, t]) => (
          <div key={k} className="p-2"><div className={cn("font-display text-2xl font-semibold", t)} data-testid={`conflict-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v ?? 0}</div><div className="text-[11px] text-ink-2">{k}</div></div>
        ))}
      </div>
    </Panel>
  );
}

export function ConflictChecksPanel() {
  const data = useConflictChecks();
  const [filter, setFilter] = useState("ALL");
  if (!data) return <Panel className="p-5 text-sm text-ink-muted">Loading…</Panel>;
  const rows = data.checks.filter((c) => filter === "ALL" || (filter === "OVERRIDDEN" ? !!c.override : filter === "PENDING" ? c.status !== "CLEAR" && !c.override && !c.matterId : c.status === filter));
  return (
    <Panel className="p-5" data-testid="conflict-checks-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="flex items-center gap-2 font-display text-xl font-semibold text-ink"><ShieldAlert className="h-5 w-5 text-copper" /> Intake Conflicts</h3><p className="text-xs text-ink-2">Every check is logged. Review overrides weekly — each one is attributed to the partner who recorded it.</p></div>
        <select className="h-8 rounded-md border border-line bg-card px-2 text-xs" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="conflict-filter">
          {["ALL", "BLOCKED", "REVIEW", "CLEAR", "OVERRIDDEN", "PENDING"].map((f) => <option key={f}>{f}</option>)}
        </select>
      </div>
      <div className="mt-4 divide-y divide-line">
        {rows.map((c) => (
          <div key={c.id} className="flex flex-wrap items-start gap-3 py-3 text-sm" data-testid={`conflict-row-${c.id}`}>
            <span className={cn("mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold", TONE[c.status])}>{c.status}</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-ink">{c.input?.clientName || c.input?.clientId || "Unnamed prospect"} <span className="text-xs text-ink-muted">· {c.findings?.length || 0} finding(s) · by {c.createdByName} · {fromNow(c.createdAt)}</span></div>
              <div className="text-xs text-ink-2">{(c.findings || []).slice(0, 3).map((f) => f.type.replaceAll("_", " ").toLowerCase()).join(" · ")}</div>
              {c.override && <div className="mt-1 rounded border border-warning/40 bg-warning/5 px-2 py-1 text-xs text-ink" data-testid={`conflict-override-${c.id}`}><b>Overridden</b> by {c.override.byName} ({c.override.role}) {fromNow(c.override.at)}: “{c.override.reason}”</div>}
            </div>
            <div className="text-xs text-ink-2">{c.matterId ? <span className="rounded bg-secondary px-1.5 py-0.5">Matter {c.matterCaseNumber || c.matterId}</span> : c.status !== "CLEAR" && !c.override ? <span className="text-danger">awaiting partner</span> : "not consumed"}</div>
          </div>
        ))}
        {!rows.length && <div className="py-6 text-center text-xs text-ink-muted">No conflict checks match this filter.</div>}
      </div>
    </Panel>
  );
}
